import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Printer, FileText } from "lucide-react";

const fmtL = (v) => (v == null ? "—" : `₹${v}L`);

function ReportSection({ title, children }) {
  return (
    <div className="border border-white/10 rounded-lg p-4 report-block">
      <p className="font-mono-tech text-[10px] tracking-[0.25em] text-[#00B0FF] uppercase mb-2">{title}</p>
      {children}
    </div>
  );
}

export function ReportCenter({ startup, testid = "report-center" }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/portal/startups/${startup.id}/report`);
      setReport(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!report) {
    return (
      <div className="glass rounded-2xl p-14 text-center" data-testid={testid}>
        <FileText className="w-10 h-10 text-slate-600 mx-auto mb-4" />
        <p className="font-display text-lg font-bold text-white mb-2">Investor-Ready Report</p>
        <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
          Compile the full startup profile — AI validation, valuation estimates, roadmap, services and documents — into a printable report.
        </p>
        <Button onClick={generate} disabled={loading} data-testid={`${testid}-generate`} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
          {loading ? "Compiling..." : "Compile Report"}
        </Button>
      </div>
    );
  }

  const s = report.startup;
  const a = s.analysis || {};
  const v = s.valuation_engine || {};
  const f = s.feasibility || {};

  return (
    <div className="space-y-4" data-testid={testid}>
      <style>{`@media print {
        body * { visibility: hidden; }
        #ll-print-report, #ll-print-report * { visibility: visible; }
        #ll-print-report { position: absolute; left: 0; top: 0; width: 100%; background: #ffffff !important; border: none !important; }
        #ll-print-report, #ll-print-report * { color: #0f172a !important; border-color: #e2e8f0 !important; background: transparent !important; box-shadow: none !important; }
        #ll-print-report .report-block { break-inside: avoid; }
      }`}</style>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-slate-500 font-mono-tech">Compiled {new Date(report.generated_at).toLocaleString("en-IN")}</p>
        <div className="flex gap-2.5">
          <Button variant="outline" size="sm" onClick={generate} disabled={loading} data-testid={`${testid}-refresh`} className="border-white/15 text-slate-300 hover:bg-white/5">{loading ? "Refreshing..." : "Refresh"}</Button>
          <Button size="sm" onClick={() => window.print()} data-testid={`${testid}-print`} className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div id="ll-print-report" className="glass rounded-2xl p-6 sm:p-8 space-y-4" data-testid={`${testid}-content`}>
        <div className="border-b border-white/10 pb-4">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase">LaunchLab Startup Report</p>
          <h2 className="font-display text-2xl font-extrabold text-white mt-1">{s.name}{s.brand_name ? ` · ${s.brand_name}` : ""}</h2>
          <p className="text-xs text-slate-400 mt-1">{s.answers?.industry || "—"} · {s.answers?.current_stage || s.status} · Generated {new Date(report.generated_at).toLocaleDateString("en-IN")}</p>
        </div>

        <ReportSection title="Concept">
          <p className="text-sm text-slate-300 leading-relaxed"><span className="text-slate-500">Problem:</span> {s.answers?.problem || "—"}</p>
          <p className="text-sm text-slate-300 leading-relaxed mt-1.5"><span className="text-slate-500">Solution:</span> {s.answers?.solution || "—"}</p>
          {s.answers?.differentiation && <p className="text-sm text-slate-300 leading-relaxed mt-1.5"><span className="text-slate-500">Differentiation:</span> {s.answers.differentiation}</p>}
        </ReportSection>

        {a.score != null && (
          <ReportSection title="AI Validation">
            <p className="text-sm text-slate-200">Score <span className="font-display font-bold text-[#FF8800]">{a.score}/100</span> · Verdict: {a.verdict}</p>
            {(a.why_it_can_work || []).length > 0 && (
              <ul className="mt-2 space-y-1">{a.why_it_can_work.slice(0, 4).map((x, i) => <li key={i} className="text-xs text-slate-400">• {x}</li>)}</ul>
            )}
            {(a.key_risks || []).length > 0 && (
              <ul className="mt-2 space-y-1">{a.key_risks.slice(0, 3).map((x, i) => <li key={i} className="text-xs text-slate-400">• Risk: {x}</li>)}</ul>
            )}
          </ReportSection>
        )}

        {(a.valuation_low_lakhs != null || v.range) && (
          <ReportSection title="Valuation & Investment Estimates">
            {a.valuation_low_lakhs != null && (
              <p className="text-sm text-slate-300">AI estimate: {fmtL(a.valuation_low_lakhs)} – {fmtL(a.valuation_high_lakhs)} {a.valuation_method ? `(${a.valuation_method})` : ""}</p>
            )}
            {v.range && (
              <p className="text-sm text-slate-300 mt-1">Valuation engine: {fmtL(v.range.low_lakhs)} – {fmtL(v.range.high_lakhs)} (mid {fmtL(v.range.mid_lakhs)}) across Scorecard / Berkus / VC methods</p>
            )}
            {a.investment_low_lakhs != null && <p className="text-sm text-slate-300 mt-1">Estimated investment need: {fmtL(a.investment_low_lakhs)} – {fmtL(a.investment_high_lakhs)}</p>}
          </ReportSection>
        )}

        {(a.roadmap || []).length > 0 && (
          <ReportSection title="Launch Roadmap">
            <div className="space-y-1.5">
              {a.roadmap.map((p, i) => (
                <p key={i} className="text-xs text-slate-300"><span className="text-[#FF8800] font-mono-tech">P{i + 1}</span> {p.phase}{p.duration ? ` · ${p.duration}` : ""}{p.specialist ? ` · ${p.specialist}` : ""}</p>
              ))}
            </div>
          </ReportSection>
        )}

        {f.executive_summary && (
          <ReportSection title="Feasibility Summary">
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{f.executive_summary}</p>
            {f.break_even_estimate && <p className="text-xs text-slate-400 mt-2">Break-even: {f.break_even_estimate}</p>}
          </ReportSection>
        )}

        <ReportSection title="Services Engaged">
          {report.orders.length === 0 ? (
            <p className="text-sm text-slate-400">No services engaged yet.</p>
          ) : (
            <div className="space-y-1.5">
              {report.orders.map((o) => (
                <div key={o.id} className="flex justify-between gap-3 text-xs">
                  <span className="text-slate-300">{o.service_name} · {o.status.replace(/_/g, " ")}{o.specialist_name ? ` · ${o.specialist_name}` : ""}</span>
                  <span className="text-slate-400 font-mono-tech">₹{o.total?.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          )}
        </ReportSection>

        <ReportSection title="Documents on File">
          {report.documents.length === 0 ? (
            <p className="text-sm text-slate-400">No documents uploaded yet.</p>
          ) : (
            <div className="space-y-1">
              {report.documents.map((d, i) => (
                <p key={i} className="text-xs text-slate-300">{d.name} · {d.category} · <span className="text-slate-500">{d.status}</span></p>
              ))}
            </div>
          )}
        </ReportSection>

        <p className="text-[10px] text-slate-500 leading-relaxed border-t border-white/10 pt-3">
          {a.disclaimer || "AI-assisted preliminary assessment — not investment, legal, tax or professional advice."} Valuation figures are indicative estimates, not certified valuations. Compiled by LaunchLab Startup OS.
        </p>
      </div>
    </div>
  );
}
